import React, { useState, useMemo } from 'react';
import { UserData } from '../api/admin';

interface AdminUserTableProps {
  users: UserData[];
  onOpenDetail: (user: UserData) => void;
  onImpersonate: (userId: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Heute';
  if (days < 30) return `${days} T`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} Mon`;
  const years = Math.floor(months / 12);
  return `${years} J`;
}

function tierBadge(tier: string) {
  return (
    <span className={`admin-tier-badge ${tier}`}>
      {tier}
    </span>
  );
}

export const AdminUserTable: React.FC<AdminUserTableProps> = ({ users, onOpenDetail, onImpersonate, searchQuery, onSearchChange }) => {
  const [sortField, setSortField] = useState<'createdAt' | 'email' | 'tier'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      u.email?.toLowerCase().includes(q) ||
      u.telegramDisplayName?.toLowerCase().includes(q) ||
      u.userId.toLowerCase().includes(q) ||
      String(u.telegramId || '').includes(q)
    );
  }, [users, searchQuery]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'createdAt') cmp = (a.createdAt || 0) - (b.createdAt || 0);
      else if (sortField === 'email') cmp = (a.email || '').localeCompare(b.email || '');
      else if (sortField === 'tier') cmp = a.tier.localeCompare(b.tier);
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [filtered, sortField, sortDir]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortIcon = (field: typeof sortField) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div>
      <div className="admin-user-search">
        <input
          type="text"
          className="admin-form-input"
          placeholder="Suche nach Email, Telegram, User ID..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
        />
        <span className="admin-user-search-count">
          {filtered.length} / {users.length} Users
        </span>
      </div>
      <div className="admin-user-table-wrapper">
        <table className="admin-user-table">
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase' as const }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>ID</th>
              <th style={{ padding: '8px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('email')}>
                Email{sortIcon('email')}
              </th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Telegram</th>
              <th style={{ padding: '8px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('tier')}>
                Tier{sortIcon('tier')}
              </th>
              <th style={{ padding: '8px', textAlign: 'center' }}>Subs</th>
              <th style={{ padding: '8px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('createdAt')}>
                Erstellt{sortIcon('createdAt')}
              </th>
              <th style={{ padding: '8px', textAlign: 'center', width: '90px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(user => {
              const summary = user.subscriptionSummary || { total: 0, active: 0 };
              return (
                <tr
                  key={user.userId}
                  style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                  onClick={() => onOpenDetail(user)}
                >
                  <td style={{ padding: '8px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)' }} title={user.userId}>
                      {user.userId.substring(0, 8)}
                    </span>
                  </td>
                  <td style={{ padding: '8px', maxWidth: '220px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, color: 'var(--text-main)' }}>
                        {user.email || '—'}
                      </span>
                      {user.email && (
                        <span title={user.emailVerified ? 'Verifiziert' : 'Nicht verifiziert'} style={{ fontSize: '10px' }}>
                          {user.emailVerified ? '✓' : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '8px', color: 'var(--text-main)' }}>
                    {user.telegramDisplayName || (user.telegramId ? `#${user.telegramId}` : '—')}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{tierBadge(user.tier)}</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: '12px', color: 'var(--text-main)' }}>
                    {summary.total > 0 ? (
                      <span title={`${summary.active} aktiv / ${summary.total} gesamt`}>
                        {summary.active}/{summary.total}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {formatRelativeTime(user.createdAt)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); onImpersonate(user.userId); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px 4px' }}
                      title="Als Benutzer anmelden"
                    >
                      👤
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenDetail(user); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px 4px' }}
                      title="Details"
                    >
                      🔍
                    </button>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                  {searchQuery.trim()
                    ? `Keine Ergebnisse für "${searchQuery}"`
                    : 'Keine Nutzer vorhanden'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
